import React, { useEffect, useRef, useState } from "react";
import { MessageBubble } from "./MessageBubble";
import { MessageInput } from "./MessageInput";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import {
  MessageCircle,
  Loader2,
  Wifi,
  WifiOff,
  Flag,
  ArrowLeft,
} from "lucide-react";
import { ReportModal } from "../ReportModal";

import { useChat } from "../../contexts/ChatContext";
import { useAuth } from "../../contexts/AuthContext";
import { ScrollArea } from "../ui/scroll-area";
import { Button } from "../ui/button";
import { exchangeApi } from "../../api/exchangeApi";
import { ExchangeBanner } from "./ExchangeBanner";
import { CreateExchangeModal } from "./ExchangeModal";

// Import the last seen utility function
import { getLastSeenText } from "../../utils/lastSeenUtils";

export const ChatWindow = ({
  selectedUser,
  messages,
  newMessage,
  onMessageChange,
  onSendMessage,
  loading,
  sendingMessage,
  isMobile = false,
  onBack,
}) => {
  const { isUserOnline, socket, isDraftConversation, listingContext } =
    useChat(); // NEW: Get draft state
  const { currentUser } = useAuth();
  const messagesEndRef = useRef(null);
  const scrollAreaRef = useRef(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showCreateExchangeModal, setShowCreateExchangeModal] = useState(false);
  const [currentExchange, setCurrentExchange] = useState(null);
  const [userDismissed, setUserDismissed] = useState(false);
  const [exchangeVisible, setExchangeVisible] = useState(true);
  const [exchangeLoading, setExchangeLoading] = useState(false);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (currentExchange?.status === "completed" && userDismissed) {
      setExchangeVisible(false);
    } else {
      setExchangeVisible(true); // Reset visibility for other states
    }
  }, [currentExchange, userDismissed]);

  // Load exchange when conversation changes - skip for draft conversations
  useEffect(() => {
    if (selectedUser?.conversationId && !isDraftConversation) {
      loadCurrentExchange();
    } else {
      setCurrentExchange(null);
    }
  }, [selectedUser?.conversationId, isDraftConversation]);

  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    });
  };

  // Load current exchange for this conversation
  const loadCurrentExchange = async () => {
    if (!selectedUser?.conversationId) return;

    setExchangeLoading(true);
    try {
      const response = await exchangeApi.getExchangeByConversation(
        selectedUser.conversationId
      );

      console.log("Fetched exchange:", response.data);

      if (
        response.success &&
        response.data?.conversation === selectedUser.conversationId
      ) {
        const fetchedExchange = response.data;
        setCurrentExchange(fetchedExchange);

        // figure out if current user is initiator or recipient
        const me = currentUser._id || currentUser.id;
        const isInitiator =
          fetchedExchange.initiator._id === me ||
          fetchedExchange.initiator === me;
        const dismissedFlag = isInitiator
          ? fetchedExchange.dismissed.initiator
          : fetchedExchange.dismissed.recipient;

        setUserDismissed(dismissedFlag);
      } else {
        setCurrentExchange(null);
        setUserDismissed(false);
      }
    } catch (error) {
      console.error("Error loading exchange:", error);
      setCurrentExchange(null);
    } finally {
      setExchangeLoading(false);
    }
  };

  // Create exchange with two listings
  const handleCreateExchange = async (
    initiatorListingId,
    recipientListingId
  ) => {
    if (!selectedUser?.conversationId) {
      throw new Error("No conversation selected");
    }

    // Make sure the IDs are not empty or undefined
    if (!initiatorListingId || !recipientListingId) {
      throw new Error("Both listing IDs are required");
    }

    const exchangeData = {
      conversationId: selectedUser.conversationId,
      initiatorListingId: initiatorListingId,
      recipientListingId: recipientListingId,
    };

    console.log("Create Exchange Data:", exchangeData);

    try {
      // Pass the exchange data as a single object
      const response = await exchangeApi.createExchange(exchangeData);

      if (response.success) {
        setCurrentExchange(response.data);

        // Send a message about the exchange creation
        const exchangeMessage =
          `Exchange request created!\n` +
          `Offering: ${response.data.initiatorListing?.title || "Unknown"}\n` +
          `For: ${response.data.recipientListing?.title || "Unknown"}`;

        // You might want to send this as an automatic message
        console.log("Exchange creation message:", exchangeMessage);
      }
    } catch (error) {
      console.error("Error creating exchange:", error);
      throw error;
    }
  };

  // Confirm exchange
  const handleConfirmExchange = async (exchangeId) => {
    try {
      const response = await exchangeApi.confirmExchange(exchangeId);

      if (response.success) {
        setCurrentExchange(response.data);

        // Send a message about the confirmation
        const message =
          response.data.status === "completed"
            ? "Exchange completed! Both parties have confirmed the trade."
            : "You have confirmed the exchange. Waiting for the other party to confirm.";

        console.log("Exchange confirmation message:", message);
      }
    } catch (error) {
      console.error("Error confirming exchange:", error);
      throw error;
    }
  };

  // Get user name
  const getUserName = (user) => {
    if (!user) return "";
    if (user.name) return user.name;
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user.firstName) return user.firstName;
    if (user.email) return user.email.split("@")[0];
    return "Unknown User";
  };

  // Get user avatar
  const getUserAvatar = (user) => {
    if (!user) return "";
    return user.image || user.avatar || "";
  };

  // Get user initials
  const getUserInitials = (user) => {
    if (!user) return "";
    const name = getUserName(user);
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // Get user status text using the utility function
  const getUserStatusText = (user) => {
    if (!user) return "";
    const selectedUserId = user._id || user.id;
    const isOnline = isUserOnline(selectedUserId);
    return getLastSeenText(user.lastActiveAt, isOnline);
  };

  // Format messages for display
  const formatMessages = () => {
    if (!messages || !Array.isArray(messages)) return [];

    return messages.map((message) => {
      // Get the current user's ID (handle both _id and id formats)
      const currentUserId = currentUser?._id || currentUser?.id;
      const messageSenderId = message.senderId?._id || message.senderId;

      // Determine if this message belongs to the current user
      const isOwn = messageSenderId === currentUserId;

      console.log("Message processing:", {
        messageId: message._id,
        messageSenderId,
        currentUserId,
        isOwn,
        messageContent: message.content || message.text,
      });

      return {
        ...message,
        isOwn,
        senderName: isOwn ? "You" : getUserName(selectedUser),
        timestamp: formatMessageTime(message.createdAt || message.timestamp),
        content: message.content || message.text || message.message, // Handle different content fields
      };
    });
  };

  // Format message timestamp
  const formatMessageTime = (timestamp) => {
    if (!timestamp) return "";

    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    if (isToday) {
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } else {
      return date.toLocaleDateString([], {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    }
  };

  const formattedMessages = formatMessages();

  // No conversation selected
  if (!selectedUser) {
    return (
      <div className="col-span-8 bg-gray-50 flex items-center justify-center h-full">
        <div className="text-center p-8">
          <MessageCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Select a conversation
          </h3>
          <p className="text-gray-600">
            Choose a conversation from the sidebar to start messaging
          </p>
          {/* Connection Status */}
          <div className="mt-4 flex items-center justify-center space-x-2">
            {socket?.connected ? (
              <>
                <Wifi className="w-4 h-4 text-green-500" />
                <span className="text-sm text-green-600">Connected</span>
              </>
            ) : (
              <>
                <WifiOff className="w-4 h-4 text-red-500" />
                <span className="text-sm text-red-600">Connecting...</span>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  const selectedUserId = selectedUser._id || selectedUser.id;
  const currentUserId = currentUser?._id || currentUser?.id;

  return (
    <div
      className={`${isMobile ? "h-full" : "col-span-8"} bg-white flex flex-col`}
    >
      {/* Chat Header - Fixed height */}
      <div className="flex-shrink-0 p-3 md:p-4 border-b border-gray-200 bg-white">
        <div className="flex items-center justify-between mt-16 md:mt-0">
          <div className="flex items-center space-x-3">
            {isMobile && onBack && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onBack}
                className="mr-3 p-1"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
            )}
            <div className="relative">
              <Avatar className="w-8 h-8 md:w-10 md:h-10">
                <AvatarImage
                  src={getUserAvatar(selectedUser)}
                  alt={getUserName(selectedUser)}
                  className="w-full h-full object-cover"
                />
                <AvatarFallback className="bg-gradient-to-br from-igbanwo-mint/20 to-emerald-100 text-igbanwo-mint text-xs md:text-sm font-medium">
                  {getUserInitials(selectedUser)}
                </AvatarFallback>
              </Avatar>
              {isUserOnline(selectedUserId) && (
                <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 md:w-3 md:h-3 bg-green-500 border-2 border-white rounded-full"></div>
              )}
            </div>
            <div>
              <h2 className="font-semibold text-gray-900 text-sm md:text-base">
                {getUserName(selectedUser)}
              </h2>
              <p className="text-xs md:text-sm text-gray-600">
                {getUserStatusText(selectedUser)}
              </p>
            </div>
          </div>

          {/* Connection Status Indicator & Action Buttons */}
          <div className="flex items-center space-x-2">
            {socket?.connected ? (
              <Wifi className="w-4 h-4 text-green-500" />
            ) : (
              <WifiOff className="w-4 h-4 text-red-500" />
            )}

            {/* Report Button - Only show for non-draft conversations */}
            {!isDraftConversation && (
              <button
                onClick={() => setShowReportModal(true)}
                className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-all duration-200"
                title="Report conversation"
              >
                <Flag className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* NEW: Show listing context banner for draft conversations */}
        {isDraftConversation && listingContext && (
          <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>About:</strong> {listingContext.title}
            </p>
            <p className="text-xs text-blue-600">
              Category: {listingContext.category} • Location:{" "}
              {listingContext.location}
            </p>
          </div>
        )}
      </div>

      {/* Messages Area - Fixed height for consistent layout */}
      <div
        className={`bg-slate-50 overflow-hidden ${
          isDraftConversation
            ? "h-[500px]" // smaller height for drafts
            : currentExchange && exchangeVisible
            ? "h-[350px]"
            : "h-[550px]"
        }`}
      >
        {loading && formattedMessages.length === 0 ? (
          <div className="h-full flex items-center justify-center">
            <div className="flex items-center space-x-2 text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Loading messages...</span>
            </div>
          </div>
        ) : formattedMessages.length > 0 ? (
          <ScrollArea className="h-full p-4">
            <div className="space-y-3 md:space-y-4 min-h-full pb-4">
              {formattedMessages.map((message, index) => (
                <MessageBubble
                  key={message._id || message.id || index}
                  message={message}
                  isOwn={message.isOwn}
                  senderName={message.senderName}
                  timestamp={message.timestamp}
                  avatar={message.isOwn ? null : getUserAvatar(selectedUser)}
                />
              ))}

              {/* Sending indicator */}
              {sendingMessage && (
                <div className="flex justify-end">
                  <div className="bg-igbanwo-mint/20 rounded-lg px-3 py-2 max-w-xs">
                    <div className="flex items-center space-x-2 text-igbanwo-mint">
                      <Loader2 className="w-3 h-3 md:w-4 md:h-4 animate-spin" />
                      <span className="text-xs md:text-sm">Sending...</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Scroll anchor */}
              <div ref={messagesEndRef} className="h-1" />
            </div>
          </ScrollArea>
        ) : (
          <div className="h-full flex items-center justify-center">
            <div className="text-center p-4">
              <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              {/* NEW: Different messages for draft vs empty conversations */}
              {isDraftConversation ? (
                <>
                  <p className="text-gray-500 mb-1">Start a conversation</p>
                  <p className="text-sm text-gray-400">
                    Send a message to {getUserName(selectedUser)} to begin
                    chatting
                  </p>
                </>
              ) : (
                <>
                  <p className="text-gray-500 mb-1">No messages yet</p>
                  <p className="text-sm text-gray-400">
                    Start the conversation with {getUserName(selectedUser)}!
                  </p>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Exchange Banner - Show above message input (skip for drafts) */}
      {currentExchange && exchangeVisible && !isDraftConversation && (
        <ExchangeBanner
          exchange={currentExchange}
          currentUserId={currentUserId}
          onConfirm={handleConfirmExchange}
          loading={exchangeLoading}
          onDismiss={() => setUserDismissed(true)}
        />
      )}

      {/* Message Input - Always visible at bottom */}
      <div className="flex-shrink-0 border-t border-gray-200 bg-white">
        <MessageInput
          newMessage={newMessage}
          onMessageChange={onMessageChange}
          onSendMessage={onSendMessage}
          disabled={sendingMessage || loading || !socket?.connected}
          onCreateExchange={() => setShowCreateExchangeModal(true)}
          showExchangeButton={!isDraftConversation} // Only show exchange button for real conversations
        />
      </div>

      {/* Report Modal - Only for non-draft conversations */}
      {!isDraftConversation && (
        <ReportModal
          isOpen={showReportModal}
          onClose={() => setShowReportModal(false)}
          targetId={selectedUser?.conversationId}
          targetType="chat"
          targetName={getUserName(selectedUser)}
        />
      )}

      {/* Exchange Modal - Pass user IDs for loading listings (skip for drafts) */}
      {!isDraftConversation && (
        <CreateExchangeModal
          isOpen={showCreateExchangeModal}
          onClose={() => setShowCreateExchangeModal(false)}
          onCreateExchange={handleCreateExchange}
          currentUserId={currentUserId}
          otherUserId={selectedUserId}
        />
      )}
    </div>
  );
};